// ReplySkeleton — loading state while the coach is drafting a reply.
//
// Per designer handoff Q4: a reply-shaped skeleton (heading line + three
// paragraph lines) + one honest status line. With real pipeline stages
// from the backend (MR A.5 / SSE), the line names the active stage. Pre-
// SSE, it falls back to a single "Thinking this through…" line so we
// never show fake progress steps.

import { useI18n } from '../i18n/index.js';
import styles from './Coach.module.css';

export type PipelineStage = 'context' | 'retrieval' | 'drafting';

export interface ReplySkeletonProps {
  /** When set, renders honest stages with the active one highlighted. When
   *  omitted (pre-SSE pipeline-events), renders a single generic line. */
  activeStage?: PipelineStage;
}

const STAGE_ORDER: PipelineStage[] = ['context', 'retrieval', 'drafting'];

export function ReplySkeleton({ activeStage }: ReplySkeletonProps) {
  const { t } = useI18n();
  return (
    <div className={styles.rep}>
      <div className={styles.repEyebrow}>{t('coach.author')}</div>
      {activeStage ? (
        <div className={styles.stage}>
          {STAGE_ORDER.map((s) => {
            const activeIdx = STAGE_ORDER.indexOf(activeStage);
            const i = STAGE_ORDER.indexOf(s);
            const state = i < activeIdx ? 'done' : i === activeIdx ? 'active' : 'pending';
            const marker = state === 'done' ? '✓ ' : state === 'active' ? '● ' : '○ ';
            const cls =
              state === 'done'
                ? styles.stageDone
                : state === 'active'
                ? styles.stageActive
                : styles.stagePending;
            return (
              <span key={s} className={cls}>
                {marker}
                {t(`coach.loading.${stageKey(s)}`)}
              </span>
            );
          })}
        </div>
      ) : (
        <div className={styles.stage}>
          <span className={styles.stageActive}>● {t('coach.loading.single')}</span>
        </div>
      )}
      <div
        className={styles.sk}
        style={{ width: '46%', height: 15, marginBottom: 14 }}
        aria-hidden="true"
      />
      <div className={styles.sk} style={{ width: '96%' }} aria-hidden="true" />
      <div className={styles.sk} style={{ width: '88%' }} aria-hidden="true" />
      <div className={styles.sk} style={{ width: '72%' }} aria-hidden="true" />
    </div>
  );
}

function stageKey(stage: PipelineStage): string {
  // Strings spec names the stages 1/2/3 (coach.loading.stage1 etc.).
  // Keep the mapping local so renaming the i18n keys doesn't ripple.
  switch (stage) {
    case 'context':
      return 'stage1';
    case 'retrieval':
      return 'stage2';
    case 'drafting':
      return 'stage3';
  }
}
